import { useSortable } from "@dnd-kit/sortable"
import { useCallback, useMemo, useState } from "react"

import { classNames } from "../../../../lib/classNames"

import type { HTMLAttributes, MouseEventHandler, Ref } from "react"

export interface TableBodyBaseRowProps
  extends HTMLAttributes<HTMLTableRowElement> {
  isSelected?: boolean
  enableRowHighlighting?: boolean
  rowKey: string
  ref?: Ref<HTMLTableRowElement>
}

// TableBodyBaseRow is a table row that is used by all body rows. To achieve our design
// for hover and selected overlays while respecting html table semantics, we apply pseudo
// elements to the td elements in the row. Applying pseudo elements at the tr level does
// not have cross-browser support, so we apply them to the td elements instead.
export const TableBodyBaseRow = ({
  children,
  className,
  onClick,
  isSelected,
  enableRowHighlighting = true,
  rowKey,
  ref,
  ...otherProps
}: TableBodyBaseRowProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)
  const onMouseEnter = useCallback<MouseEventHandler<HTMLTableRowElement>>(
    () => setIsHovered(true),
    []
  )
  const onMouseLeave = useCallback<
    MouseEventHandler<HTMLTableRowElement>
  >(() => {
    setIsHovered(false)
    setIsPressed(false)
  }, [])

  const onMouseDown = useCallback<MouseEventHandler<HTMLTableRowElement>>(
    () => setIsPressed(true),
    []
  )
  const onMouseUp = useCallback<MouseEventHandler<HTMLTableRowElement>>(
    () => setIsPressed(false),
    []
  )

  const { setNodeRef, isDragging, isOver, activeIndex, index } = useSortable({
    id: rowKey,
  })

  const mergedRef = useMemo(() => mergeRefs(ref, setNodeRef), [ref, setNodeRef])

  return (
    <tr
      ref={mergedRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      className={classNames(
        "border-v2-border-default group border-b focus:outline-none focus-visible:ring-0",
        onClick && "cursor-pointer",
        ...(onClick &&
        !isSelected &&
        enableRowHighlighting &&
        !isDragging &&
        (isHovered || isPressed)
          ? [
              "[&>td]:relative",
              "[&>td]:after:absolute [&>td]:after:inset-0",
              isHovered &&
                "[&>td]:after:bg-black/[0.04] dark:[&>td]:after:bg-white/[0.06]",
              isPressed &&
                "[&>td]:after:bg-black/[0.08] dark:[&>td]:after:bg-white/[0.12]",
              "[&>td]:after:pointer-events-none",
            ]
          : []),
        ...(!isSelected && enableRowHighlighting && !isDragging
          ? [
              "[&:focus-visible>td]:relative",
              "[&:focus-visible>td]:after:absolute [&:focus-visible>td]:after:inset-0",
              "[&:focus-visible>td]:after:bg-black/[0.04] dark:[&:focus-visible>td]:after:bg-white/[0.06]",
              "[&:focus-visible>td]:after:pointer-events-none",
            ]
          : []),
        ...(!isDragging && isSelected && enableRowHighlighting
          ? [
              "[&>td]:relative",
              "[&>td]:after:absolute [&>td]:after:inset-0",
              "[&>td]:after:bg-[#7FB2FF]/10 dark:[&>td]:after:bg-[#2A54E9]/10",
              isHovered &&
                "[&>td]:after:bg-[#7FB2FF]/20 dark:[&>td]:after:bg-[#2A54E9]/20",
              "[&:focus-visible>td]:after:bg-[#7FB2FF]/20 dark:[&:focus-visible>td]:after:bg-[#2A54E9]/20",
              isPressed &&
                "[&>td]:after:bg-[#7FB2FF]/30 dark:[&>td]:after:bg-[#2A54E9]/30",
              "[&>td]:after:pointer-events-none",
            ]
          : []),
        ...(!isDragging && !isOver
          ? [
              "[&:focus-visible>td:first-child]:relative",
              "[&:focus-visible>td:first-child]:before:absolute",
              "[&:focus-visible>td:first-child]:before:inset-y-0",
              "[&:focus-visible>td:first-child]:before:left-0",
              "[&:focus-visible>td:first-child]:before:w-[3px]",
              "[&:focus-visible>td:first-child]:before:bg-v2-border-focus",
              "[&:focus-visible>td:first-child]:before:pointer-events-none",
            ]
          : []),
        ...(isOver && !isDragging
          ? [
              "[&>td]:relative [&>td]:overflow-visible",
              "[&>td]:before:absolute [&>td]:before:left-0 [&>td]:before:right-0 [&>td]:before:h-1",
              "[&>td]:before:bg-blue-400/50",
              "[&>td]:before:pointer-events-none",
              "[&>td]:before:z-10",
              activeIndex > index
                ? "[&>td]:before:-top-0.5"
                : "[&>td]:before:-bottom-0.5",
            ]
          : []),
        className
      )}
      onClick={onClick}
      {...otherProps}
    >
      {children}
    </tr>
  )
}

function mergeRefs<T>(
  ...refs: (Ref<T> | undefined)[]
): (node: T | null) => void {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return
      if (typeof ref === "function") {
        ref(node)
        return
      }
      ;(ref as React.RefObject<T | null>).current = node
    })
  }
}
